import AppConfig from '@/config'
import { STORAGE_KEYS } from '@/constants/storageKeys'

declare global {
	interface Window {
		ym?: (counterId: number, action: string, ...params: any[]) => void
	}
}

const NOSCRIPT_ID = 'yandex-metrika-noscript'

class YandexMetrika {
	private counterId: string
	private isInitialized = false
	private consent = false

	constructor() {
		this.counterId = AppConfig.yaMetrikaId
		this.loadSettings()
	}

	// Во всех тенантах аналитика выключена до явного согласия: и отказ ('false'),
	// и отсутствие выбора (нет ключа) значат «не грузить счётчик».
	private loadSettings(): void {
		this.consent = localStorage.getItem(STORAGE_KEYS.ENABLE_ANALYTICS_COOKIES) === 'true'
	}

	// Согласие пользователя — отдельно от наличия счётчика у тенанта:
	// решение сохраняем всегда, грузим скрипт только когда есть что грузить.
	private get isEnabled(): boolean {
		return this.consent && !!this.counterId
	}

	get analyticsConsent(): boolean {
		return this.consent
	}

	// Событие реально уйдёт в Метрику: согласие есть и ym загружен.
	get canSend(): boolean {
		return this.isEnabled && !!window.ym
	}

	init(): void {
		if (this.isInitialized) return

		if (!this.isEnabled) {
			return
		}

		this.injectScript()
		this.isInitialized = true
	}

	private injectScript(): void {
		// Удаляем старый скрипт если есть
		const oldScript = document.getElementById('yandex-metrika-script')
		if (oldScript) oldScript.remove()

		// Удаляем старый noscript
		document.getElementById(NOSCRIPT_ID)?.remove()

		// Создаем новый скрипт
		const script = document.createElement('script')
		script.id = 'yandex-metrika-script'
		script.innerHTML = `
      (function(m,e,t,r,i,k,a){m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};
      m[i].l=1*new Date();
      k=e.createElement(t),a=e.getElementsByTagName(t)[0],k.async=1,k.src=r,a.parentNode.insertBefore(k,a)})
      (window, document, "script", "https://mc.yandex.ru/metrika/tag.js", "ym");
      ym(${this.counterId}, "init", {
        clickmap: true,
        accurateTrackBounce: true,
        webvisor: true,
		trackLinks: true
      });
    `

		document.head.appendChild(script)

		// Добавляем noscript
		const noscript = document.createElement('noscript')
		noscript.id = NOSCRIPT_ID
		noscript.innerHTML = `<div><img src="https://mc.yandex.ru/watch/${this.counterId}" style="position:absolute; left:-9999px;" alt="" /></div>`
		document.body.appendChild(noscript)
	}

	// Единственная точка изменения согласия — вызывается из стора cookies.
	setEnabled(enabled: boolean): void {
		this.consent = enabled
		// Пишем явное значение, а не removeItem: отказ должен отличаться от «не выбирал».
		localStorage.setItem(STORAGE_KEYS.ENABLE_ANALYTICS_COOKIES, String(enabled))

		if (this.isEnabled && !this.isInitialized) {
			this.init()
		} else if (!this.isEnabled && this.isInitialized) {
			this.destroy()
		}
	}

	private destroy(): void {
		// Удаляем скрипт
		const script = document.getElementById('yandex-metrika-script')
		if (script) script.remove()

		// Удаляем noscript
		document.getElementById(NOSCRIPT_ID)?.remove()

		// Снимаем глобальную функцию, чтобы hit/reachGoal больше ничего не отправляли.
		// Уже загруженный tag.js остаётся в памяти вкладки и его собственные сборщики
		// (webvisor, clickmap) замолчат только после перезагрузки страницы.
		delete window.ym

		this.isInitialized = false
	}

	// Простые методы для отправки событий
	reachGoal(goal: string, params?: any): void {
		if (!this.canSend) return
		window.ym!(parseInt(this.counterId), 'reachGoal', goal, params)
	}

	hit(url: string): void {
		if (!this.canSend) return
		window.ym!(parseInt(this.counterId), 'hit', url)
	}
}

// Экспортируем инстанс с вашим ID счетчика
export const yandexMetrika = new YandexMetrika()
